/**
 * event-recurrence-fields.tsx — Self-contained UI for building a simplified RRULE string.
 *
 * Supported options (D9):
 *   none | daily | weekly (+ BYDAY checkboxes) | monthly
 *   INTERVAL (positive integer)
 *   End condition: none (open) | COUNT (after N times) | UNTIL (until date)
 *
 * Emits a single rrule string (or undefined when freq is "none").
 *
 * D10 Calendar Reminders:
 *   EventReminderFields component — multi-select preset offsets (minutes before).
 *   Storage: CalendarEvent.reminders?: number[]
 *   NOTE: Notification *firing* is deferred to a later phase (requires
 *   Notification API / service-worker setup outside this file's scope).
 */

import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { BentoDateInput, BentoSelect } from "./bento-ui";

// ------------------------------------------------------------------
// D10 Reminder presets (minutes before event)
// ------------------------------------------------------------------

/**
 * Compact preset chips shown in the single-row reminder bar.
 * Dropped "1 giờ" (60) and "1 ngày" (1440) from the chip row —
 * those values are still reachable via the custom minute input.
 */
export const REMINDER_PRESETS: { value: number; label: string }[] = [
  { value: 0,  label: "Đúng giờ" },
  { value: 5,  label: "5 phút" },
  { value: 15, label: "15 phút" },
  { value: 30, label: "30 phút" },
];

/** Format a reminder offset (minutes) as a readable Vietnamese string.
 * Splits into ngày/giờ/phút so non-round values (e.g. 61 → "Trước 1 giờ 1 phút")
 * never show a long decimal. */
export function fmtReminder(min: number): string {
  if (min <= 0) return "Đúng giờ";
  const days = Math.floor(min / 1440);
  const hours = Math.floor((min % 1440) / 60);
  const mins = min % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days} ngày`);
  if (hours) parts.push(`${hours} giờ`);
  if (mins) parts.push(`${mins} phút`);
  return `Trước ${parts.join(" ")}`;
}

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

export type RecurrenceFreq = "none" | "daily" | "weekly" | "monthly";
export type RecurrenceEnd = "none" | "count" | "until";

export type RecurrenceState = {
  freq: RecurrenceFreq;
  interval: number;
  byday: string[]; // e.g. ["MO","WE","FR"]
  endType: RecurrenceEnd;
  count: number;
  until: string; // ISO date "YYYY-MM-DD"
};

type Props = {
  value: RecurrenceState;
  onChange: Dispatch<SetStateAction<RecurrenceState>>;
};

// ------------------------------------------------------------------
// BYDAY weekday config
// ------------------------------------------------------------------
const WEEKDAYS: { code: string; label: string }[] = [
  { code: "MO", label: "T2" },
  { code: "TU", label: "T3" },
  { code: "WE", label: "T4" },
  { code: "TH", label: "T5" },
  { code: "FR", label: "T6" },
  { code: "SA", label: "T7" },
  { code: "SU", label: "CN" },
];

// ------------------------------------------------------------------
// buildRrule — compose rrule string from RecurrenceState
// ------------------------------------------------------------------

/** Returns rrule string or undefined when freq=none. */
export function buildRrule(state: RecurrenceState): string | undefined {
  if (state.freq === "none") return undefined;

  const freqMap: Record<Exclude<RecurrenceFreq, "none">, string> = {
    daily: "DAILY",
    weekly: "WEEKLY",
    monthly: "MONTHLY",
  };

  const parts: string[] = [`FREQ=${freqMap[state.freq]}`];

  if (state.interval > 1) parts.push(`INTERVAL=${state.interval}`);

  if (state.freq === "weekly" && state.byday.length > 0) {
    parts.push(`BYDAY=${state.byday.join(",")}`);
  }

  if (state.endType === "count" && state.count >= 1) {
    parts.push(`COUNT=${state.count}`);
  } else if (state.endType === "until" && state.until) {
    // Convert "YYYY-MM-DD" to "YYYYMMDD" for RRULE standard
    parts.push(`UNTIL=${state.until.replace(/-/g, "")}`);
  }

  return parts.join(";");
}

// ------------------------------------------------------------------
// Default factory
// ------------------------------------------------------------------
export function defaultRecurrenceState(): RecurrenceState {
  return { freq: "none", interval: 1, byday: [], endType: "none", count: 10, until: "" };
}

/** Parse an existing rrule string back into RecurrenceState (best-effort). */
export function rruleToState(rrule: string | undefined): RecurrenceState {
  const base = defaultRecurrenceState();
  if (!rrule) return base;

  const parts: Record<string, string> = {};
  for (const seg of rrule.split(";")) {
    const eq = seg.indexOf("=");
    if (eq !== -1) parts[seg.slice(0, eq).toUpperCase()] = seg.slice(eq + 1);
  }

  const freqRaw = parts["FREQ"]?.toLowerCase() as RecurrenceFreq | undefined;
  const freq: RecurrenceFreq =
    freqRaw && ["daily", "weekly", "monthly"].includes(freqRaw) ? freqRaw : "none";
  const interval = parts["INTERVAL"] ? Math.max(1, parseInt(parts["INTERVAL"], 10)) : 1;
  const byday = parts["BYDAY"] ? parts["BYDAY"].split(",").map((s) => s.trim().toUpperCase()) : [];

  let endType: RecurrenceEnd = "none";
  let count = 10;
  let until = "";
  if (parts["COUNT"]) {
    endType = "count";
    count = parseInt(parts["COUNT"], 10);
  } else if (parts["UNTIL"]) {
    endType = "until";
    const raw = parts["UNTIL"].replace(/[^0-9]/g, "");
    until = raw.length === 8 ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}` : parts["UNTIL"];
  }

  return { freq, interval, byday, endType, count, until };
}

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

const cls = {
  label: "mb-1 block text-[12px] font-semibold text-ink-500",
  input: "w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[14px] outline-none focus:border-brand-500",
  select: "w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[14px] outline-none focus:border-brand-500",
};

/**
 * Number input that stays editable: keeps a local string buffer so the user can
 * CLEAR the field and type a new value (the old code reset to 1 on every empty
 * keystroke, so you could never delete the existing digit). Clamps to [min,max]
 * only on blur — never mid-typing.
 */
function ClampedNumberInput({
  value, min, max, className, onCommit,
}: { value: number; min: number; max: number; className?: string; onCommit: (n: number) => void }) {
  const [text, setText] = useState(String(value));
  // Re-sync when the external value changes (e.g. opening the form on a saved rule).
  useEffect(() => { setText(String(value)); }, [value]);
  return (
    <input
      type="number"
      min={min}
      max={max}
      className={className}
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw); // allow empty / partial input while typing
        const n = parseInt(raw, 10);
        if (Number.isFinite(n)) onCommit(n);
      }}
      onBlur={() => {
        const n = parseInt(text, 10);
        const clamped = Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : min;
        setText(String(clamped));
        onCommit(clamped);
      }}
    />
  );
}

// ------------------------------------------------------------------
// D10 EventReminderFields component
// ------------------------------------------------------------------

type ReminderProps = {
  /** Current list of selected reminder offsets (minutes before). */
  reminders: number[];
  /** Called with the new deduplicated, sorted reminder array. */
  onChange: (next: number[]) => void;
};

/**
 * Multi-select reminder offsets (D10) — single scrollable row layout.
 *
 * Row layout (never wraps):
 *   [preset chips] [custom non-preset chips] [number input] [Thêm btn]
 *
 * Deduplicates on toggle/add; result is always sorted ascending.
 * Custom values (not in REMINDER_PRESETS) that are already selected
 * render as togglable chips (click to remove) in the same row.
 *
 * NOTE: Notification firing is NOT implemented here — this component
 * only persists the reminder offsets. Firing requires Notification API
 * / service-worker integration (future phase).
 */
export function EventReminderFields({ reminders, onChange }: ReminderProps) {
  const [customMinutes, setCustomMinutes] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  const presetValues = new Set(REMINDER_PRESETS.map((p) => p.value));

  // Selected values not covered by a preset chip — render as extra chips
  const customChips = reminders.filter((v) => !presetValues.has(v));

  const toggle = (value: number) => {
    const next = reminders.includes(value)
      ? reminders.filter((v) => v !== value)
      : [...reminders, value];
    onChange([...new Set(next)].sort((a, b) => a - b));
  };

  const addCustom = () => {
    const parsed = parseInt(customMinutes, 10);
    if (!Number.isFinite(parsed) || parsed < 0) return;
    const next = [...reminders, parsed];
    onChange([...new Set(next)].sort((a, b) => a - b));
    setCustomMinutes("");
    customInputRef.current?.focus();
  };

  return (
    <div className="col-span-2 space-y-2 rounded-2xl border border-ink-200 p-3">
      <span className="mb-1 block text-[12px] font-semibold text-ink-500">Nhắc nhở trước sự kiện</span>

      {/* Single scrollable row — flex, no wrap, horizontal scroll on overflow */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
        {/* Preset chips */}
        {REMINDER_PRESETS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => toggle(value)}
            className={`shrink-0 rounded-lg px-2.5 py-1 text-[12px] font-bold transition ${
              reminders.includes(value)
                ? "bg-brand-500 text-white"
                : "bg-ink-200/50 text-ink-600 hover:bg-ink-200"
            }`}
          >
            {label}
          </button>
        ))}

        {/* Custom-value chips (selected, not in preset list) */}
        {customChips.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => toggle(value)}
            title="Nhấn để bỏ chọn"
            className="shrink-0 rounded-lg bg-brand-500 px-2.5 py-1 text-[12px] font-bold text-white transition hover:bg-brand-600"
          >
            {fmtReminder(value)}
          </button>
        ))}

        {/* Divider */}
        <span className="mx-0.5 h-5 w-px shrink-0 bg-ink-200" aria-hidden="true" />

        {/* Custom minute input */}
        <input
          ref={customInputRef}
          type="number"
          min="0"
          value={customMinutes}
          onChange={(e) => setCustomMinutes(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder="phút"
          className="w-16 shrink-0 rounded-lg border border-ink-200 bg-white px-2 py-1 text-[12px] font-semibold text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
        <button
          type="button"
          onClick={addCustom}
          className="shrink-0 rounded-lg bg-ink-200/50 px-2.5 py-1 text-[12px] font-bold text-ink-600 transition hover:bg-ink-200"
        >
          Thêm
        </button>
      </div>

      {reminders.length === 0 && (
        <p className="text-[11px] text-ink-400">Không nhắc (chưa chọn)</p>
      )}
    </div>
  );
}

export function EventRecurrenceFields({ value, onChange }: Props) {
  const set = <K extends keyof RecurrenceState>(key: K, v: RecurrenceState[K]) =>
    onChange((prev) => ({ ...prev, [key]: v }));

  const toggleDay = (code: string) =>
    onChange((prev) => ({
      ...prev,
      byday: prev.byday.includes(code) ? prev.byday.filter((d) => d !== code) : [...prev.byday, code],
    }));

  return (
    <div className="col-span-2 space-y-3 rounded-2xl border border-ink-200 p-3">
      {/* Frequency selector */}
      <label className="block">
        <span className={cls.label}>Lặp lại</span>
        <BentoSelect
          className="text-[14px]"
          value={value.freq}
          onChange={(e) => set("freq", e.target.value as RecurrenceFreq)}
        >
          <option value="none">Không lặp</option>
          <option value="daily">Hàng ngày</option>
          <option value="weekly">Hàng tuần</option>
          <option value="monthly">Hàng tháng</option>
        </BentoSelect>
      </label>

      {/* Show detail controls only when recurrence is active */}
      {value.freq !== "none" && (
        <>
          {/* Interval */}
          <label className="block">
            <span className={cls.label}>Mỗi (số chu kỳ)</span>
            <ClampedNumberInput
              value={value.interval}
              min={1}
              max={99}
              className={cls.input}
              onCommit={(n) => set("interval", n)}
            />
          </label>

          {/* BYDAY checkboxes — weekly only */}
          {value.freq === "weekly" && (
            <div>
              <span className={cls.label}>Các ngày trong tuần</span>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {WEEKDAYS.map(({ code, label }) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => toggleDay(code)}
                    className={`rounded-lg px-2.5 py-1 text-[12px] font-bold transition ${
                      value.byday.includes(code)
                        ? "bg-brand-500 text-white"
                        : "bg-ink-200/50 text-ink-600 hover:bg-ink-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* End condition */}
          <label className="block">
            <span className={cls.label}>Kết thúc</span>
            <BentoSelect
              className="text-[14px]"
              value={value.endType}
              onChange={(e) => set("endType", e.target.value as RecurrenceEnd)}
            >
              <option value="none">Không giới hạn (trong khoảng xem)</option>
              <option value="count">Sau N lần</option>
              <option value="until">Đến ngày</option>
            </BentoSelect>
          </label>

          {value.endType === "count" && (
            <label className="block">
              <span className={cls.label}>Số lần lặp</span>
              <ClampedNumberInput
                value={value.count}
                min={1}
                max={999}
                className={cls.input}
                onCommit={(n) => set("count", n)}
              />
            </label>
          )}

          {value.endType === "until" && (
            <label className="block">
              <span className={cls.label}>Ngày kết thúc</span>
              <BentoDateInput
                className={cls.input}
                value={value.until}
                onChange={(e) => set("until", e.target.value)}
              />
            </label>
          )}
        </>
      )}
    </div>
  );
}
